/** Minutes from midnight, half-open ranges [start, end) within one calendar day. */
export const MINUTES_PER_DAY = 1440

/** 0 = Monday … 6 = Sunday */
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/** Tooltip text for a set of weekly availability rows (preset or light-preset-group). */
export function summarizePresetAvailabilityRows(
  rows: { weekday: number }[],
): string {
  if (rows.length === 0) return 'No weekly windows stored for this preset.'
  const byDay = new Map<number, number>()
  for (const r of rows) {
    byDay.set(r.weekday, (byDay.get(r.weekday) ?? 0) + 1)
  }
  const days = [...byDay.keys()]
    .sort((a, b) => a - b)
    .map((d) => WEEKDAY_LABELS[d])
    .join(', ')
  return `${rows.length} weekly window${rows.length === 1 ? '' : 's'} (${days}).`
}

export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6

export interface MinuteInterval {
  start: number
  end: number
}

export interface AvailabilitySlot {
  weekday: WeekdayIndex
  startMinutes: number
  endMinutes: number
}

export interface CurfewConfig {
  start: string | null | undefined
  end: string | null | undefined
}

const HH_MM = /^([01]?\d|2[0-3]):([0-5]\d)$/

export function parseHHMMToMinutes(value: string | null | undefined): number | null {
  if (value == null || String(value).trim() === '') return null
  const m = HH_MM.exec(String(value).trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  const total = h * 60 + min
  if (total < 0 || total >= MINUTES_PER_DAY) return null
  return total
}

/** Same-day availability window end: allows `24:00` as minute 1440 (exclusive end of calendar day). */
export function parseAvailabilityEndHHMMToMinutes(value: string | null | undefined): number | null {
  if (value == null || String(value).trim() === '') return null
  const t = String(value).trim()
  if (t === '24:00') return MINUTES_PER_DAY
  return parseHHMMToMinutes(t)
}

/** Format availability end for API / editors; minute 1440 is serialized as `24:00`. */
export function formatAvailabilityEndToHHMM24(total: number): string {
  const floored = Math.floor(total)
  if (floored >= MINUTES_PER_DAY) return '24:00'
  return formatMinutesToHHMM24(floored)
}

/** e.g. 7am, 12pm, 9:30pm (on-the-hour omits :00). */
export function formatMinutesToHHMM(total: number): string {
  const t = Math.floor(total)
  if (t >= MINUTES_PER_DAY || t === 0) return 'Midnight'
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY - 1, t))
  const h24 = Math.floor(clamped / 60)
  const m = clamped % 60
  const isPm = h24 >= 12
  let h12 = h24 % 12
  if (h12 === 0) h12 = 12
  const period = isPm ? 'pm' : 'am'
  if (m === 0) {
    return `${h12}${period}`
  }
  return `${h12}:${m.toString().padStart(2, '0')}${period}`
}

/** 24-hour HH:MM for APIs and `TimePicker` values. */
export function formatMinutesToHHMM24(total: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY - 1, Math.floor(total)))
  const h = Math.floor(clamped / 60)
  const min = clamped % 60
  return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
}

export function mergeIntervals(intervals: MinuteInterval[]): MinuteInterval[] {
  if (intervals.length === 0) return []
  const sorted = [...intervals].sort((a, b) => a.start - b.start)
  const out: MinuteInterval[] = [{ ...sorted[0] }]
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i]
    const last = out[out.length - 1]
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end)
    } else {
      out.push({ ...cur })
    }
  }
  return out
}

/** Curfew segments that fall on this calendar weekday (local wall-clock model). */
export function curfewIntervalsForWeekday(
  curfew: CurfewConfig,
): MinuteInterval[] {
  const start = parseHHMMToMinutes(curfew.start ?? null)
  const end = parseHHMMToMinutes(curfew.end ?? null)
  if (start == null || end == null) return []
  if (start === end) return []

  if (start > end) {
    return mergeIntervals([
      { start: 0, end },
      { start, end: MINUTES_PER_DAY },
    ])
  }
  return [{ start, end }]
}

export function unionIntervals(a: MinuteInterval[], b: MinuteInterval[]): MinuteInterval[] {
  return mergeIntervals([...a, ...b])
}

export function complementIntervals(restricted: MinuteInterval[]): MinuteInterval[] {
  const merged = mergeIntervals(restricted)
  const out: MinuteInterval[] = []
  let cursor = 0
  for (const iv of merged) {
    if (iv.start > MINUTES_PER_DAY || iv.end <= 0) continue
    const s = Math.max(0, iv.start)
    const e = Math.min(MINUTES_PER_DAY, iv.end)
    if (cursor < s) {
      out.push({ start: cursor, end: s })
    }
    cursor = Math.max(cursor, e)
  }
  if (cursor < MINUTES_PER_DAY) {
    out.push({ start: cursor, end: MINUTES_PER_DAY })
  }
  return out
}

export function restrictedIntervalsForDay(
  site: CurfewConfig,
  area: CurfewConfig,
): MinuteInterval[] {
  return unionIntervals(
    curfewIntervalsForWeekday(site),
    curfewIntervalsForWeekday(area),
  )
}

export function availabilityFromNonCurfewWindows(
  site: CurfewConfig,
  area: CurfewConfig,
): AvailabilitySlot[] {
  const slots: AvailabilitySlot[] = []
  for (let w = 0; w < 7; w++) {
    const restricted = restrictedIntervalsForDay(site, area)
    const free = complementIntervals(restricted)
    for (const iv of free) {
      if (iv.end > iv.start) {
        slots.push({
          weekday: w as WeekdayIndex,
          startMinutes: iv.start,
          endMinutes: iv.end,
        })
      }
    }
  }
  return slots
}

export function availabilityFrom24x7Windows(): AvailabilitySlot[] {
  const slots: AvailabilitySlot[] = []
  for (let w = 0; w < 7; w++) {
    slots.push({
      weekday: w as WeekdayIndex,
      startMinutes: 0,
      endMinutes: MINUTES_PER_DAY,
    })
  }
  return slots
}

export function mergeAvailabilitySlots(slots: AvailabilitySlot[]): AvailabilitySlot[] {
  const byDay = new Map<number, MinuteInterval[]>()
  for (const s of slots) {
    if (s.endMinutes <= s.startMinutes) continue
    const list = byDay.get(s.weekday) ?? []
    list.push({ start: s.startMinutes, end: s.endMinutes })
    byDay.set(s.weekday, list)
  }
  const out: AvailabilitySlot[] = []
  for (const [weekday, intervals] of byDay) {
    for (const iv of mergeIntervals(intervals)) {
      if (iv.end > iv.start) {
        out.push({
          weekday: weekday as WeekdayIndex,
          startMinutes: iv.start,
          endMinutes: iv.end,
        })
      }
    }
  }
  out.sort((a, b) => a.weekday - b.weekday || a.startMinutes - b.startMinutes)
  return out
}

export function slotKey(s: AvailabilitySlot): string {
  return `${s.weekday}-${s.startMinutes}-${s.endMinutes}`
}

/** Half-open minute ranges [start, end). */
export function halfOpenMinuteIntervalsOverlap(a: MinuteInterval, b: MinuteInterval): boolean {
  return a.start < b.end && b.start < a.end
}

export type AvailabilityCurfewOverlapFlags = {
  overlapsSite: boolean
  overlapsArea: boolean
}

/**
 * True when any merged availability window intersects site or area curfew (same day, wall-clock model).
 */
export function availabilitySlotsOverlapCurfewFlags(
  slots: AvailabilitySlot[],
  site: CurfewConfig,
  area: CurfewConfig,
): AvailabilityCurfewOverlapFlags {
  const siteIvs = curfewIntervalsForWeekday(site)
  const areaIvs = curfewIntervalsForWeekday(area)
  let overlapsSite = false
  let overlapsArea = false
  for (const s of mergeAvailabilitySlots(slots)) {
    if (s.endMinutes <= s.startMinutes) continue
    const win: MinuteInterval = { start: s.startMinutes, end: s.endMinutes }
    if (!overlapsSite && siteIvs.some((iv) => halfOpenMinuteIntervalsOverlap(win, iv))) {
      overlapsSite = true
    }
    if (!overlapsArea && areaIvs.some((iv) => halfOpenMinuteIntervalsOverlap(win, iv))) {
      overlapsArea = true
    }
    if (overlapsSite && overlapsArea) break
  }
  return { overlapsSite, overlapsArea }
}

/** Map API preset / LPG availability rows to calendar slots. */
export function apiWindowsToAvailabilitySlots(
  rows: { weekday: number; startTime: string; endTime: string }[],
): AvailabilitySlot[] {
  const out: AvailabilitySlot[] = []
  for (const r of rows) {
    if (!Number.isInteger(r.weekday) || r.weekday < 0 || r.weekday > 6) continue
    const start = parseHHMMToMinutes(r.startTime)
    const end = parseAvailabilityEndHHMMToMinutes(r.endTime)
    if (start == null || end == null || end <= start) continue
    out.push({
      weekday: r.weekday as WeekdayIndex,
      startMinutes: start,
      endMinutes: end,
    })
  }
  return mergeAvailabilitySlots(out)
}

export function availabilitySlotsToApiPayload(
  slots: AvailabilitySlot[],
): { weekday: number; startTime: string; endTime: string }[] {
  return mergeAvailabilitySlots(slots).map((s) => ({
    weekday: s.weekday,
    startTime: formatMinutesToHHMM24(s.startMinutes),
    endTime: formatAvailabilityEndToHHMM24(s.endMinutes),
  }))
}

/** Normalize through the same HH:MM payload round-trip used when saving presets. */
function availabilitySlotsCanonicalThroughApi(slots: AvailabilitySlot[]): AvailabilitySlot[] {
  return mergeAvailabilitySlots(
    apiWindowsToAvailabilitySlots(availabilitySlotsToApiPayload(slots)),
  )
}

function availabilitySlotSetsEqual(a: AvailabilitySlot[], b: AvailabilitySlot[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (
      x.weekday !== y.weekday ||
      x.startMinutes !== y.startMinutes ||
      x.endMinutes !== y.endMinutes
    ) {
      return false
    }
  }
  return true
}

/**
 * True when stored API rows match “all times outside site + area curfew” (calendar “Use all non-curfew times”).
 * Uses the same HH:MM payload round-trip as saves (including `24:00` end-of-day).
 */
export function presetRowsMatchFullNonCurfew(
  rows: { weekday: number; startTime: string; endTime: string }[],
  site: CurfewConfig,
  area: CurfewConfig,
): boolean {
  if (rows.length === 0) return false
  const expected = mergeAvailabilitySlots(availabilityFromNonCurfewWindows(site, area))
  if (expected.length === 0) return false
  const actual = apiWindowsToAvailabilitySlots(rows)
  return availabilitySlotSetsEqual(
    availabilitySlotsCanonicalThroughApi(expected),
    availabilitySlotsCanonicalThroughApi(actual),
  )
}

export function presetRowsMatch24x7(
  rows: { weekday: number; startTime: string; endTime: string }[],
): boolean {
  if (rows.length === 0) return false
  const expected = mergeAvailabilitySlots(availabilityFrom24x7Windows())
  if (expected.length === 0) return false
  const actual = apiWindowsToAvailabilitySlots(rows)
  return availabilitySlotSetsEqual(
    availabilitySlotsCanonicalThroughApi(expected),
    availabilitySlotsCanonicalThroughApi(actual),
  )
}